// src/components/healthrecords/PatientConfoundsTab.jsx
//
// A patient's confound history — known things (illness, medication,
// non-fasting draw, etc.) that suppress escalation on the risk-scoring
// path. Backed by GET/POST /api/patients/{patientId}/confounds and
// PATCH .../confounds/{id}/resolve (V124, PatientConfoundController).
//
// THREE SOURCES, per the agreed design: PATIENT_SELF_SERVICE, NAVIGATOR,
// and DOCTOR — all three reach this component's own "+ Log confound" form
// via the `source` prop, which every call site sets explicitly rather than
// this component guessing which portal it's in. For DOCTOR this is
// reserved for rare cases needing actual clinical confirmation; for
// NAVIGATOR it's the normal way to log something a patient mentioned on a
// call; for PATIENT_SELF_SERVICE (PatientSettingsPage) it's the primary,
// lowest-friction path, in the patient's own plain-language framing — see
// the COPY table below, keyed by source, rather than a single wording
// stretched to fit all three audiences. `source` defaults to 'DOCTOR' only
// for backward compatibility with the original doctor-only call site —
// always pass it explicitly at a new call site.
//
// Portal-agnostic like EffectivePanelTab (same `patient` prop convention)
// — reused from the navigator portal via the `source`/`currentUserId`
// props rather than any portal-detection of its own.
//
// "Active" mirrors the backend's own ConfoundDetector.isActive: unresolved,
// or resolved but still within the Constitution's 45-day post-resolution
// suppression window. Computed server-side and trusted as-is here — no
// window-math duplicated in this component.
//
// RE-EVALUATE THIS REPORT (optional `report` prop: {id, drawDate}). Only
// meaningful in a context that has ONE specific triage in view (the doctor's
// DoctorTriageView passes it; the navigator's patient-level PatientDetailPage
// does not, since it has no single report selected — no button shows there).
// Eligibility is a pure date-range check done here in the browser, using data
// already fetched — a confound applies to a report only when the report's
// draw date falls within [dateOnset, dateResolved ?? today]. The actual
// re-score happens server-side (TriageReevaluationController); this
// component only decides when to SHOW the button.

import React, { useEffect, useState, useCallback } from 'react';
import { confoundAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import Icon from '../Icon';

const CONFOUND_TYPE_LABEL = {
  RECENT_ILLNESS:   'Recent illness',
  STEROID_USE:      'Steroid use',
  EXERCISE:         'Strenuous exercise',
  DEHYDRATION:      'Dehydration',
  NON_FASTING_DRAW: 'Non-fasting draw',
  STRESS:           'Acute stress',
  ALCOHOL_BINGE:    'Alcohol binge',
  MENSTRUAL_PHASE:  'Menstrual phase',
  PREGNANCY:        'Pregnancy',
  POST_SURGERY:     'Post-surgery',
  RAMADAN:          'Ramadan / extended fasting',
  OTHER:            'Other',
};

const SOURCE_LABEL = {
  PATIENT_SELF_SERVICE: 'Patient',
  NAVIGATOR:            'Navigator',
  DOCTOR:               'Doctor',
};

// Source-dependent copy for the log form and empty state — the same
// component reads very differently depending on who's looking at it: a
// patient reporting something about themselves, a navigator logging what a
// patient said on a call, or a doctor confirming a rare clinical case.
const COPY = {
  PATIENT_SELF_SERVICE: {
    logButton: '+ Report Something',
    formTitle: 'Tell us about something that might affect your results',
    formIntro: 'Recent illness, a medication, not fasting before a blood draw — anything like this '
      + 'can make a result look worse than it really is. Let us know and your care team will take it '
      + 'into account.',
    notePlaceholder: 'Anything else you want your care team to know…',
    emptyTitle: 'Nothing reported',
    emptyDesc: "You haven't reported anything that might affect how your results are read.",
  },
  NAVIGATOR: {
    logButton: '+ Log Confound',
    formTitle: 'Log a confound',
    formIntro: 'Log something the patient mentioned during this call — recent illness, medication, '
      + 'a missed fast before a draw, etc.',
    notePlaceholder: 'Clinical context for this confound…',
    emptyTitle: 'No confounds reported',
    emptyDesc: 'Nothing on file yet — patient self-report at intake and navigator logging are the usual sources.',
  },
  DOCTOR: {
    logButton: '+ Log Confound',
    formTitle: 'Log a confound',
    formIntro: 'Reserved for cases needing clinical confirmation — most confounds are reported by the '
      + 'patient at intake or logged by the navigator during a call.',
    notePlaceholder: 'Clinical context for this confound…',
    emptyTitle: 'No confounds reported',
    emptyDesc: 'Nothing on file yet — patient self-report at intake and navigator logging are the usual sources.',
  },
};

function copyFor(source) { return COPY[source] || COPY.DOCTOR; }

function confoundTypeLabel(type) { return CONFOUND_TYPE_LABEL[type] || type; }
function sourceLabel(source) { return SOURCE_LABEL[source] || source; }

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// Date-only comparison — strips time-of-day so a report drawn earlier the
// same day as onset/resolution still counts as within range.
function dateOnly(d) {
  if (!d) return null;
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime()) ? null : new Date(parsed.toDateString());
}

function reportEligibleForConfound(confound, report) {
  if (!confound.active || !report?.drawDate) return false;
  const drawDate = dateOnly(report.drawDate);
  const onset = dateOnly(confound.dateOnset);
  if (!drawDate || !onset || drawDate < onset) return false;
  const resolvedOrToday = confound.dateResolved ? dateOnly(confound.dateResolved) : dateOnly(todayStr());
  return drawDate <= resolvedOrToday;
}

function ConfoundRow({ confound, report, onResolve, resolving, onReevaluate, reevaluating }) {
  const eligible = report && reportEligibleForConfound(confound, report);
  return (
    <tr>
      <td data-label="Type"><strong>{confoundTypeLabel(confound.confoundType)}</strong></td>
      <td data-label="Source">{sourceLabel(confound.source)}</td>
      <td data-label="Onset">{fmtDate(confound.dateOnset)}</td>
      <td data-label="Resolved">{fmtDate(confound.dateResolved)}</td>
      <td data-label="Status">
        <span className={`badge ${confound.active ? 'badge-moderate' : 'badge-unknown'}`}>
          {confound.active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td data-label="Note">
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{confound.freeTextNote || '—'}</span>
      </td>
      <td data-label="Action">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          {!confound.dateResolved && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={resolving}
              onClick={() => onResolve(confound.id)}
            >
              {resolving ? 'Resolving…' : 'Mark Resolved'}
            </button>
          )}
          {eligible && (
            <button
              type="button"
              className="btn btn-accent btn-sm"
              disabled={reevaluating}
              onClick={() => onReevaluate(confound.id)}
              title="Re-score the report currently in view using this confound"
            >
              {reevaluating ? 'Re-evaluating…' : 'Re-evaluate Report'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function PatientConfoundsTab({ patient, currentUserId, source = 'DOCTOR', report, onReevaluated }) {
  const { toast } = useToast();

  const [confounds, setConfounds] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [resolvingId, setResolvingId] = useState(null);
  const [reevaluatingId, setReevaluatingId] = useState(null);

  const [showForm, setShowForm]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    confoundType: 'RECENT_ILLNESS',
    dateOnset: todayStr(),
    dateResolved: '',
    freeTextNote: '',
  });

  const load = useCallback(async () => {
    if (!patient?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await confoundAPI.list(patient.id);
      setConfounds(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load confound history.');
    } finally {
      setLoading(false);
    }
  }, [patient?.id]);

  useEffect(() => { load(); }, [load]);

  const handleResolve = useCallback(async (confoundId) => {
    if (!patient?.id) return;
    setResolvingId(confoundId);
    try {
      await confoundAPI.resolve(patient.id, confoundId, todayStr());
      toast.success('Confound marked resolved.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not mark this confound resolved.');
    } finally {
      setResolvingId(null);
    }
  }, [patient?.id, toast, load]);

  const handleReevaluate = useCallback(async (confoundId) => {
    if (!patient?.id || !report?.id) return;
    setReevaluatingId(confoundId);
    try {
      const res = await confoundAPI.reevaluate(patient.id, report.id, confoundId, currentUserId || null);
      const { previousRiskLevel, newRiskLevel } = res.data || {};
      toast.success(
        previousRiskLevel && previousRiskLevel !== newRiskLevel
          ? `Report re-evaluated — ${previousRiskLevel} → ${newRiskLevel}.`
          : 'Report re-evaluated.'
      );
      onReevaluated?.(report.id, res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not re-evaluate this report.');
    } finally {
      setReevaluatingId(null);
    }
  }, [patient?.id, report?.id, currentUserId, toast, onReevaluated]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!patient?.id) return;
    setSubmitting(true);
    try {
      await confoundAPI.log(patient.id, {
        confoundType: form.confoundType,
        source,
        recordedByStaffId: currentUserId || null,
        dateOnset: form.dateOnset || todayStr(),
        dateResolved: form.dateResolved || null,
        freeTextNote: form.freeTextNote || null,
      });
      toast.success('Confound logged.');
      setForm({ confoundType: 'RECENT_ILLNESS', dateOnset: todayStr(), dateResolved: '', freeTextNote: '' });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not log this confound.');
    } finally {
      setSubmitting(false);
    }
  }, [patient?.id, currentUserId, source, form, toast, load]);

  if (loading) {
    return <div className="loading-state"><div className="spinner" />Loading confound history…</div>;
  }

  if (error) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="alert" size={20} /></div>
          <div className="empty-title">Couldn't load confound history</div>
          <div className="empty-desc">{error}</div>
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  const activeCount = confounds.filter(c => c.active).length;
  const inactiveCount = confounds.length - activeCount;

  return (
    <div>
      <div className="stats-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className="stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-value" style={{ fontSize: 24, color: 'var(--amber-text)' }}>{activeCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Resolved / Archived</div>
          <div className="stat-value" style={{ fontSize: 24, color: 'var(--text-muted)' }}>{inactiveCount}</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '12px 0 18px' }}>
        <button type="button" className="btn btn-accent btn-sm" onClick={() => setShowForm(s => !s)}>
          {showForm ? 'Cancel' : copyFor(source).logButton}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-title">{copyFor(source).formTitle}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 12 }}>
            {copyFor(source).formIntro}
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Confound type</label>
                <select
                  className="form-select"
                  value={form.confoundType}
                  onChange={e => setForm(f => ({ ...f, confoundType: e.target.value }))}
                >
                  {Object.keys(CONFOUND_TYPE_LABEL).map(type => (
                    <option key={type} value={type}>{confoundTypeLabel(type)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Onset date</label>
                <input
                  type="date" className="form-input"
                  value={form.dateOnset}
                  onChange={e => setForm(f => ({ ...f, dateOnset: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Resolved date (optional)</label>
                <input
                  type="date" className="form-input"
                  value={form.dateResolved}
                  onChange={e => setForm(f => ({ ...f, dateResolved: e.target.value }))}
                />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Note</label>
                <textarea
                  className="form-textarea"
                  value={form.freeTextNote}
                  onChange={e => setForm(f => ({ ...f, freeTextNote: e.target.value }))}
                  placeholder={copyFor(source).notePlaceholder}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                {submitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {confounds.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="clock" size={20} /></div>
            <div className="empty-title">{copyFor(source).emptyTitle}</div>
            <div className="empty-desc">{copyFor(source).emptyDesc}</div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-title">Confound log ({confounds.length})</div>
          <table className="stack-table">
            <thead>
              <tr>
                <th>Type</th><th>Source</th><th>Onset</th><th>Resolved</th>
                <th>Status</th><th>Note</th><th></th>
              </tr>
            </thead>
            <tbody>
              {confounds.map(c => (
                <ConfoundRow
                  key={c.id}
                  confound={c}
                  report={report}
                  onResolve={handleResolve}
                  resolving={resolvingId === c.id}
                  onReevaluate={handleReevaluate}
                  reevaluating={reevaluatingId === c.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
